import {
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  notification,
  Radio,
  Row,
  Select,
} from 'antd';
import { FloatingFormItem, InputDecimal, PlanAccountsSelect } from '../_common';
import { useCallback, useEffect, useState } from 'react';
import { useTefPayment, useTefActivation } from '@/core/domain/tef/tef.hooks';
import {
  TefPaymentFinancedType,
  TefPaymentResponse,
  TefPaymentType,
} from '@/core/domain/tef/tef.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import ModalLoadingOverlay from '../_common/ModalLoadingOverlay';
import DocumentTypesSelectField from '../_common/DocumentTypesSelectField';
import { DocumentTypeAccreditorType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import TEFConnectionOverlay from './TEFConnectionOverlay';
import { useAlertBeforeDestroySession } from '@/hooks/useAlertBeforeDestroySession';

export type TefOnResultParams = TefPaymentResponse & FormValues;

interface Props {
  opened: boolean;
  onClose: VoidFunction;
  amout: number;
  onResult(result: TefOnResultParams): Promise<void>;
  cnpj?: string;
}

interface FormValues {
  typeSale: TefPaymentType;
  amount: number;
  installments?: number;
  financialType?: TefPaymentFinancedType;
  documentTypeId: number;
  planAccountId: number;
}

export default function TEF(props: Props) {
  const [form] = Form.useForm<FormValues>();

  const fieldRequired = useDefaultEmptyFieldError();

  const typeSale = Form.useWatch('typeSale', form);
  const financialType = Form.useWatch('financialType', form);

  const tefPayment = useTefPayment();

  const isLoading = tefPayment.isLoading;

  const tefActivation = useTefActivation(props.cnpj, props.opened);

  const [infoOpen, infoAction] = useDisclosure(true);

  const [isPending, setIsPending] = useState(false);

  useAlertBeforeDestroySession(isLoading);

  useEffect(() => {
    const interval = setInterval(() => {
      if (
        !tefActivation.isLoading &&
        !tefActivation.isSuccess &&
        props.opened
      ) {
        tefActivation.refetch();
      }
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [tefActivation.isSuccess, props.opened]);

  useEffect(() => {
    form.setFieldValue('amount', props.amout);
  }, [props.amout, props.opened]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const result = await tefPayment.mutateAsync({
        financiamento: values.financialType,
        parcelas: values.installments,
        valor: values.amount,
        type: values.typeSale,
      });

      if (!result) {
        return Modal.confirm({
          title: 'Ativação necessária',
          content:
            'Por favor, inicialize o software de integração Elgin TEF GP',
          okButtonProps: { style: { display: 'none' } },
        });
      }

      if (!result.resultado || !result.resultado.tef) {
        return notification.error({
          message: 'Pagamento',
          description: 'Não foi encontrado dados de retorno do pagamento',
        });
      }

      if (result.resultado.tef.resultadoTransacao !== '0') {
        return notification.error({
          message: 'Pagamento',
          description: result.resultado.tef.mensagemResultado,
        });
      }

      setIsPending(true);

      await props.onResult({ ...result, ...values });

      handleClose();
    } catch (error) {
      console.log(error);
    } finally {
      setIsPending(false);
    }
  };

  const handleClose = useCallback(() => {
    if (isLoading) return;

    form.resetFields();
    props.onClose();
  }, [isLoading]);

  return (
    <Modal
      open={props.opened}
      onCancel={handleClose}
      title="Pagamento Elgin TEF (Pin-Pad)"
      okText="Solicitar pagamento"
      onOk={handleSubmit}
      confirmLoading={isLoading}
      cancelButtonProps={{ disabled: isLoading }}
      closable={!isLoading}
      style={{ position: 'relative' }}
      maskClosable={!tefActivation.isSuccess && !tefActivation.isLoading}
    >
      {isPending && (
        <ModalLoadingOverlay
          style={{ borderRadius: 10 }}
          messages={['Processando pagamento...']}
          posAbsolute
        />
      )}

      {tefActivation.isLoading && (
        <ModalLoadingOverlay
          style={{ borderRadius: 10 }}
          messages={['Analisando integração...']}
          posAbsolute
        />
      )}

      <TEFConnectionOverlay
        visible={!tefActivation.isSuccess && !tefActivation.isLoading}
        onClose={handleClose}
      />

      {props.opened && infoOpen && (
        <ModalLoadingOverlay
          disableLoading
          style={{ borderRadius: 10, textAlign: 'center' }}
          messages={[
            <div
              style={{ textAlign: 'center' }}
              id="tef-feedback-softwares-open"
            >
              <div>
                Antes de solicitar o pagamento verifique se os programas Elgin
                TEFHUB (GP Elgin) e E1_Connect estão abertos corretamente.
              </div>

              <Button onClick={infoAction.close} id="tef-feedback-is-ok">
                Estão abertos!
              </Button>
            </div>,
          ]}
          posAbsolute
        />
      )}

      <Form form={form}>
        <Row gutter={[16, 16]} id="tef-form-container">
          <Col span={24}>
            <FloatingFormItem name="typeSale" initialValue="debit">
              <Radio.Group
                style={{ width: '100%', display: 'inline-flex' }}
                buttonStyle="solid"
                optionType="button"
                disabled={isLoading}
                options={[
                  {
                    value: 'debit',
                    label: 'Débito',
                    style: { flex: 1, textAlign: 'center' },
                  },
                  {
                    value: 'credit',
                    label: 'Crédito',
                    style: { flex: 1, textAlign: 'center' },
                  },
                  {
                    value: 'pix',
                    label: 'PIX',
                    style: { flex: 1, textAlign: 'center' },
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="documentTypeId"
              label="Tipo de documento"
              rules={[fieldRequired]}
            >
              <DocumentTypesSelectField
                disabled={!typeSale || isLoading}
                queryParams={{
                  accreditorType: DocumentTypeAccreditorType.TEF_ELGIN,
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="planAccountId"
              label="Plano de contas"
              rules={[fieldRequired]}
            >
              <PlanAccountsSelect
                disabled={!typeSale || isLoading}
                queryFilter={{
                  active: true,
                  destination: PlanAccountDestination.Revenue,
                  hierarchy: PlanAccountHierarchy.Analitical,
                }}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="amount"
              label="Valor da venda"
              initialValue={0}
              rules={[
                fieldRequired,
                {
                  type: 'number',
                  min: 0.01,
                  message: 'Valor mínimo de R$ 0,01',
                },
              ]}
            >
              <InputDecimal min={0.01} disabled={isLoading} />
            </FloatingFormItem>
          </Col>

          {typeSale === 'credit' && (
            <>
              <Col span={12}>
                <FloatingFormItem
                  name="financialType"
                  label="Tipo de financiamento"
                  initialValue="1"
                  rules={[fieldRequired]}
                  getValueFromEvent={(value) => {
                    if (value === '1') {
                      form.setFieldValue('installments', 1);
                    }
                    return value;
                  }}
                >
                  <Select
                    options={[
                      { value: '1', label: 'A vista' },
                      { value: '2', label: 'Parcelamento emissor' },
                      { value: '3', label: 'Parcelamento estabelecimento' },
                    ]}
                    disabled={isLoading}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="installments"
                  label="Parcelas"
                  initialValue={1}
                  rules={[
                    fieldRequired,
                    { type: 'number', min: 1, message: 'Mínimo de 1 parcela' },
                  ]}
                >
                  <InputNumber
                    min={1}
                    style={{ width: '100%' }}
                    disabled={financialType === '1' || isLoading}
                  />
                </FloatingFormItem>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </Modal>
  );
}
